import React from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import ContactsPage from '../views/pages/ContactsPage';
import LoginPage from '../views/pages/auth/LoginPage';
import AuthenticatedRoute from './AuthenticatedRoute';
import NotFoundPage from '../views/pages/NotFoundPage';
import NavBar from '../views/components/layouts/NavBar';
import MagicLoginPage from '../views/pages/auth/MagicLoginPage';
import PaymentCompletionPage from '../views/pages/payment/PaymentCompletionPage';
import SelectedCommunityRoute from './SelectedCommunityRoute';
import SelectedBusinessRoute from './SelectedBusinessRoute';
import SelectedBusinessOrCommunityRoute from './SelectedBusinessOrCommunityRoute';
import GeneralSettingsPage from '../views/pages/settings/GeneralSettingsPage';
import RequestedDataPage from '../views/pages/settings/RequestedDataPage';
import AcceptAdminInvitePage from '../views/pages/AcceptAdminInvitePage';
import useAuth from '../hooks/account/useAuth';
import ProfilePage from '../views/pages/ProfilePage';
import OnboardingPage from '../views/pages/onboarding/OnboardingPage';
import ApplicationLayout from '../views/components/layouts/ApplicationLayout/ApplicationLayout';
import InviteLinkPage from '../views/pages/flows/InviteLinkPage';
import CreateEventPage from '../views/pages/agenda/CreateEventPage';
import EventDetailsPage from '../views/pages/agenda/EventDetailsPage';
import FormsOverviewPage from '../views/pages/forms/FormsOverviewPage';
import CreateFormPage from '../views/pages/forms/CreateFormPage';
import FormResponsesPage from '../views/pages/forms/FormResponsesPage';
import UploadExcelPage from '../views/pages/settings/UploadExcelPage';
import { SubscriptionsPage } from '../views/pages/payment/SubscriptionsPage';
import ExportExcelPage from '../views/pages/settings/ExportExcelPage';
import FeedbackPage from '../views/pages/FeedbackPage';
import IntegrationsPage from '../views/pages/IntegrationsPage';
import UpgradeSubscriptionPage from '../views/pages/payment/UpgradeSubscriptionPage';
import ManageSubscriptionPage from '../views/pages/payment/ManageCommunitySubscriptionPage';
import ManageBusinessSubscriptionPage from '../views/pages/payment/ManageBusinessSubscriptionPage';
import InvoicesPage from '../views/pages/payment/InvoicesPage';
import OverviewPage from '../views/pages/OverviewPage';
import SendMasPage from '../views/pages/SendMasPage';
import ConfigureIntegrationsPage from '../views/pages/ConfigureIntegrationsPage';
import ScoreListsPage from '../views/pages/lists/ScoreListsPage';
import TaskOverviewPage from '../views/pages/lists/TaskOverviewPage';
import CreateCommunicationPage from '../views/pages/communication/CreateCommunicationPage';
import CommunicationsPage from '../views/pages/communication/CommunicationsPage';
import ActivateDashboardPage from '../views/pages/flows/ActivateDashboardPage';
import MutationsPage from '../views/pages/financial/MutationsPage';
import CompleteCardlessPage from '../views/pages/financial/CompleteCardlessPage';
import AccountingPage from '../views/pages/financial/AccountingPage';
import CategoriesPage from '../views/pages/financial/CategoriesPage';
import PotsPage from '../views/pages/financial/PotsPage';
// eslint-disable-next-line import/extensions
import CreatePotPage from '../views/pages/financial/CreatePotPage';
// eslint-disable-next-line import/extensions
import PotDetailsPage from '../views/pages/financial/PotDetailsPage';
import AffiliatePage from '../views/pages/AffiliatePage';
import CreateAffiliateCodePage from '../views/pages/flows/CreateAffiliateCodePage';
import ContributionPlanPage from '../views/pages/financial/ContributionPlanPage';
import PhotoAlbumsOverviewPage from '../views/pages/photo-albums/PhotoAlbumsOverview';
import PhotoAlbumPage from '../views/pages/photo-albums/PhotoAlbumPage';
import CreatePhotoAlbumPage from '../views/pages/photo-albums/CreatePhotoAlbumPage';
import GeneralEventPage from '../views/pages/agenda/GeneralEventPage';
import ScheduleDetailsPage from '../views/pages/agenda/schedules/ScheduleDetailsPage';
import CreateShiftPage from '../views/pages/agenda/schedules/CreateShiftPage';
import FinancialMemberInformationPage from '../views/pages/financial/FinancialMemberInformationPage';
import CommunitiesPage from '../views/pages/business/CommunitiesPage';
import SponsorsPage from '../views/pages/business/SponsorsPage';
import SponsorDetailsPage from '../views/pages/business/SponsorDetailsPage';
import MerchantPayoutPage from '../views/pages/financial/MerchantPayoutPage';
import MarketplaceOrdersPage from '../views/pages/financial/MarketplaceOrdersPage';
import FinancialOverviewPage from '../views/pages/financial/FinancialOverviewPage';
import TransactionsPage from '../views/pages/financial/TransactionsPage';
import SettingsPage from '../views/pages/financial/SettingsPage';
import FinancialMemberBalancePage from '../views/pages/financial/FinancialMemberBalancePage';
import DeclarationsPage from '../views/pages/financial/DeclarationsPage';
import SalesCollectPage from '../views/pages/sales/SalesCollectPage';
import ReportsPage from '../views/pages/financial/ReportsPage';
import FilesPage from '../views/pages/FilesPage';
import LandingPage from '../views/pages/landing/LandingPage';
import CommunityDetailsPage from '../views/pages/business/CommunityDetailsPage';
import CommunityRedirectPage from '../views/pages/common/CommunityRedirectPage';

export default function Router(): JSX.Element {
  const auth = useAuth();
  const location = useLocation().pathname;

  return (
    <>
      {(!auth || location === '/admin/invite') &&
        location !== '/onboarding' &&
        !location.startsWith('/race') && <NavBar />}
      <Routes>
        <Route path="login" element={<LoginPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="magic" element={<MagicLoginPage />} />
        <Route path="/admin/invite" element={<AcceptAdminInvitePage />} />
        <Route path="/c/:communityId" element={<CommunityRedirectPage />} />
        {!auth && <Route path="" element={<LoginPage />} />}
        <Route path="" element={<AuthenticatedRoute />}>
          <Route path="" element={<ApplicationLayout />}>
            <Route path="" element={<LandingPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/dashboard/activate" element={<ActivateDashboardPage />} />

            {/* Routes that are allowed when EITHER a community OR a business is selected */}
            <Route path="" element={<SelectedBusinessOrCommunityRoute />}>
              {/* Members pages */}
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/invite-link" element={<InviteLinkPage />} />
              <Route path="/settings/requested-data" element={<RequestedDataPage />} />

              {/* Settings */}
              <Route path="/settings" element={<GeneralSettingsPage />} />
              <Route path="/settings/upload-excel" element={<UploadExcelPage />} />
              <Route path="/settings/export-excel" element={<ExportExcelPage />} />

              {/* Misc */}
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/send-mas" element={<SendMasPage />} />

              {/* Payment routes */}
              <Route path="/payment/completion" element={<PaymentCompletionPage />} />
              <Route path="/settings/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/subscription/upgrade" element={<UpgradeSubscriptionPage />} />
              <Route path="/subscription/invoices" element={<InvoicesPage />} />

              {/* Affiliate */}
              <Route path="/affiliate" element={<AffiliatePage />} />
              <Route path="/affiliate/create" element={<CreateAffiliateCodePage />} />
            </Route>

            {/* Routes that still strictly require a selected COMMUNITY */}
            <Route path="" element={<SelectedCommunityRoute />}>
              <Route path="/overview" element={<OverviewPage />} />

              <Route path="/subscription/manage" element={<ManageSubscriptionPage />} />

              <Route path="/albums" element={<PhotoAlbumsOverviewPage />} />
              <Route path="/create-album" element={<CreatePhotoAlbumPage />} />
              <Route path="/album/:albumId" element={<PhotoAlbumPage />} />

              <Route path="/events" element={<GeneralEventPage />} />
              {/* events */}
              <Route path="/events" element={<GeneralEventPage />} />
              <Route path="/events/create" element={<CreateEventPage />} />
              <Route path="/events/details" element={<EventDetailsPage />} />
              <Route path="/events/schedule/:scheduleId" element={<ScheduleDetailsPage />} />
              <Route path="/events/schedule/:scheduleId/shifts/create" element={<CreateShiftPage />} />

              {/* forms */}
              <Route path="/forms" element={<FormsOverviewPage />} />
              <Route path="/forms/create" element={<CreateFormPage />} />
              <Route path="/forms/:formId/edit" element={<CreateFormPage />} />
              <Route path="/forms/:formId" element={<FormResponsesPage />} />

              {/* Lists */}
              <Route path="/lists/score" element={<ScoreListsPage />} />
              <Route path="/lists/task" element={<TaskOverviewPage />} />

              <Route path="/financial/overview" element={<FinancialOverviewPage />} />
              <Route path="/financial/transactions" element={<TransactionsPage />} />
              <Route path="/financial/members" element={<FinancialMemberBalancePage />} />
              <Route path="/financial/settings" element={<SettingsPage />} />
              <Route path="/financial/book-years" element={<SettingsPage />} />
              <Route path="/financial/budget" element={<SettingsPage />} />
              <Route path="/financial/categorization-rules" element={<SettingsPage />} />
              <Route path="/financial/declarations" element={<DeclarationsPage />} />
              <Route path="/financial/mutations/:bankAccountId" element={<MutationsPage />} />
              <Route path="/financial/accounting" element={<AccountingPage />} />
              <Route path="/financial/categories" element={<CategoriesPage />} />
              <Route path="/financial/pots" element={<PotsPage />} />
              <Route path="/financial/pots/create" element={<CreatePotPage />} />
              <Route path="/financial/pots/:potId" element={<PotDetailsPage />} />
              <Route path="/financial/sales-collect" element={<SalesCollectPage />} />
              <Route path="/sales" element={<SalesCollectPage />} />
              <Route path="/financial/tickets" element={<SalesCollectPage />} />
              <Route path="/financial/reports" element={<ReportsPage />} />
              <Route
                path="/financial/overview/complete-go-cardless"
                element={<CompleteCardlessPage />}
              />
              <Route
                path="/financial/contribution-plans/:contributionPlanId"
                element={<ContributionPlanPage />}
              />
              <Route
                path="/financial/member/:dashboardMemberId"
                element={<FinancialMemberInformationPage />}
              />
              <Route path="/financial/payout/:payoutId" element={<MerchantPayoutPage />} />
              <Route path="/financial/orders" element={<MarketplaceOrdersPage />} />
              {/* Redirects for old financial routes */}
              <Route
                path="/financial/mutations"
                element={<Navigate to="/financial/settings" replace />}
              />
              <Route
                path="/financial/administration"
                element={<Navigate to="/financial/transactions" replace />}
              />
              <Route
                path="/financial/member-balance"
                element={<Navigate to="/financial/members" replace />}
              />
              <Route
                path="/financial/balance"
                element={<Navigate to="/financial/reports" replace />}
              />
              <Route
                path="/financial/contribution-plans"
                element={<Navigate to="/financial/sales-collect" replace />}
              />
              <Route
                path="/financial/merchantAccount"
                element={<Navigate to="/financial/sales-collect" replace />}
              />
              <Route
                path="/financial/products"
                element={<Navigate to="/financial/sales-collect" replace />}
              />
              <Route path="/communication" element={<CommunicationsPage />} />
              <Route path="/communication/create" element={<CreateCommunicationPage />} />
              <Route path="/communication/edit" element={<CreateCommunicationPage />} />

              <Route path="/files" element={<FilesPage />} />
            </Route>

            {/* Routes that strictly require a selected BUSINESS */}
            <Route path="" element={<SelectedBusinessRoute />}>
              <Route path="/my-communities" element={<CommunitiesPage />} />
              <Route path="/my-communities/manage-community" element={<CommunityDetailsPage />} />
              <Route path="/business/subscription" element={<ManageBusinessSubscriptionPage />} />
              <Route path="/business/payment/completion" element={<PaymentCompletionPage businessMode />} />
              <Route path="/sponsors" element={<SponsorsPage />} />
              <Route path="/sponsors/:sponsorId" element={<SponsorDetailsPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/integrations/manage" element={<ConfigureIntegrationsPage />} />
            </Route>

          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
